import React, {RefObject} from 'react';
import BaseProps from '../../types/BaseProps';
import classNames from 'classnames';

import './ColumnResizer.scss';

type ColumnResizerProps = BaseProps;

type ColumnResizerState = {
  dragging: boolean;
  mouseX: number;
  startPos: number;
  startWidthPrev: number;
  startWidthNext: number;
};

/**
 * Simple table column resizer component.
 *
 * Taken from https://github.com/nicole-mcg/react-column-resizer
 */
export default class ColumnResizer extends React.Component<ColumnResizerProps, ColumnResizerState> {
  private readonly columnRef: RefObject<HTMLTableDataCellElement> = React.createRef();

  constructor(props: ColumnResizerProps) {
    super(props);
    this.state = {
      dragging: false,
      mouseX: 0,
      startPos: 0,
      startWidthPrev: 0,
      startWidthNext: 0
    };
  }

  componentDidMount() {
    document.addEventListener('mouseup', this.endDrag);
  }

  componentWillUnmount() {
    document.removeEventListener('mouseup', this.endDrag);
    document.removeEventListener('mousemove', this.onMouseMove);
  }

  startDrag = (event: any): void => {
    this.setState({
      dragging: true,
      startPos: event.screenX,
      mouseX: event.screenX,
      startWidthPrev: 0,
      startWidthNext: 0
    });

    if (this.columnRef.current) {
      const prevSibling: any = this.columnRef.current.previousSibling;
      if (prevSibling) {
        this.setState({startWidthPrev: prevSibling.clientWidth});
      }

      const nextSibling: any = this.columnRef.current.nextSibling;
      if (nextSibling) {
        this.setState({startWidthNext: nextSibling.clientWidth});
      }

      document.addEventListener('mousemove', this.onMouseMove);
    }
  };

  endDrag = () => {
    this.setState({dragging: false});
    document.removeEventListener('mousemove', this.onMouseMove);
  };

  onMouseMove = (event: MouseEvent): void => {
    if (!this.state.dragging) {
      return;
    }

    const mouseX = event.screenX;
    this.setState({mouseX});

    const element = this.columnRef.current;
    if (!element) {
      return;
    }

    const moveDiff = this.state.startPos - mouseX;
    const newPrev = this.state.startWidthPrev - moveDiff;
    const newNext = this.state.startWidthNext + moveDiff;

    const prevSibling: any = element.previousSibling;
    if (prevSibling) {
      prevSibling.style.width = `${newPrev}px`;
    }

    const nextSibling: any = element.nextSibling;
    if (nextSibling) {
      nextSibling.style.width = `${newNext}px`;
    }
  };

  render() {
    const classes = classNames('ColumnResizer', this.props.className);
    return <th ref={this.columnRef} className={classes} onMouseDown={this.startDrag}/>;
  }
}

export function ColumnResizerFiller() {
  return <td className="ColumnResizerFiller"/>;
}
